import { CircularProgress, Stack, Typography } from '@mui/material'
import {
  useCurrentStore,
  useDeliveryByOwner,
  useGoogleMap,
  useOrdering,
} from 'hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { DeliveryByOwnerInfo, Place } from 'models'
import { debounce, pick } from 'lodash'
import { t } from 'i18next'

interface OrderDeliveryInfoProps {
  onError?: (message?: string) => void
}

export const OrderDeliveryByOwnerInfo = ({
  onError,
}: OrderDeliveryInfoProps) => {
  const { store, formatCurrency } = useCurrentStore()
  const { order, saveOrderProps } = useOrdering()

  const { calculateDistance } = useGoogleMap()
  const fetch = useMemo(
    () => debounce(calculateDistance, 400),
    [calculateDistance],
  )

  const { calculateDeliveryFee } = useDeliveryByOwner()

  const [state, setState] = useState<
    DeliveryByOwnerInfo & { isLoading: boolean }
  >({ isLoading: false })

  const setErrorMessage = useCallback(
    (message?: string) => {
      setState((s) => ({ ...s, errorMessage: message }))
      onError?.(message)
    },
    [onError, setState],
  )

  const deliveryPlace = order?.customer_place
  const deliverySettings = store?.delivery_settings
  const orderType = order?.order_type
  const orderSubTotal = order?.subtotal ?? 0
  const byOwnerMinAmountToAcceptDelivery =
    deliverySettings?.accept_delivery_amount ?? 0
  const origin = pick(deliverySettings?.delivery_from_address, [
    'place_id',
    'description',
  ]) as Place

  useEffect(() => {
    if (
      orderType === 'delivery' &&
      orderSubTotal < byOwnerMinAmountToAcceptDelivery
    ) {
      const errorMessage = t(
        'Order subtotal must be at least {{amount}} for delivery.',
        { amount: formatCurrency(byOwnerMinAmountToAcceptDelivery) },
      )
      setErrorMessage(errorMessage)
    } else {
      setErrorMessage()
    }
  }, [
    orderType,
    orderSubTotal,
    byOwnerMinAmountToAcceptDelivery,
    formatCurrency,
    setErrorMessage,
  ])

  useEffect(() => {
    if (
      !deliveryPlace ||
      orderType !== 'delivery' ||
      orderSubTotal < byOwnerMinAmountToAcceptDelivery
    ) {
      setState((state) => ({ ...state, isLoading: false }))
      saveOrderProps({
        delivery_fee_amount: undefined,
        delivery_distance: undefined,
      })
      return
    }

    const address = deliveryPlace?.description
      ? deliveryPlace.description
      : [
          deliveryPlace.address,
          deliveryPlace.city,
          deliveryPlace.state,
          deliveryPlace.zip_code,
        ]
          .filter(Boolean)
          .join(',')

    if (!deliveryPlace?.place_id && !address) {
      setErrorMessage(
        t(
          'Delivery fee cannot be calculated for this address. Try selecting a different one',
        ),
      )
      return
    }

    let active = true

    const destination = pick(deliveryPlace, [
      'place_id',
      'description',
    ]) as Place

    setState((state) => ({ ...state, isLoading: true }))
    fetch(origin, destination, (response) => {
      if (active && response && response.routes && response.routes.length > 0) {
        const rawDistance = response.routes.reduce((s, r) => {
          return r.legs.reduce((ls, l) => l.distance.value + ls, s)
        }, 0)
        // m to miles and rounded up
        const roundedDistance = Math.round(rawDistance * 0.00062137)
        const deliveryInfo = calculateDeliveryFee(roundedDistance)
        setState({ ...deliveryInfo, isLoading: false })
        saveOrderProps({
          delivery_fee_amount: deliveryInfo.deliveryFeeAmount,
          delivery_distance: roundedDistance,
        })
        onError?.(deliveryInfo.errorMessage)
      } else {
        setErrorMessage(
          t(
            'Delivery fee cannot be calculated for this address. Try selecting a different one',
          ),
        )
        setState((state) => ({ ...state, isLoading: false }))
      }
    })

    return () => {
      active = false
    }
  }, [orderType, deliveryPlace, orderSubTotal]) // eslint-disable-line react-hooks/exhaustive-deps
  // The above line lint check is disabled to avoid cirular dependency

  if (state.isLoading) {
    return <CircularProgress size={40} sx={{ mt: 2 }} />
  }

  return (
    <Stack spacing={1} mt={2}>
      {state.errorMessage && (
        <Typography variant="h5" color="error">
          {state.errorMessage}
        </Typography>
      )}
      {state.deliveryDistanceText && (
        <Typography variant="body2" color="secondary">
          <Trans
            defaults="Delivery distance: <strong>{{distanceText}}</strong>."
            values={{ distanceText: state.deliveryDistanceText }}
          />
        </Typography>
      )}
      {state.deliveryFeeAmount && (
        <Typography variant="body2" color="secondary">
          <Trans
            defaults="Delivery fee: <strong>{{amount}}</strong>."
            values={{ amount: formatCurrency(state.deliveryFeeAmount) }}
          />
        </Typography>
      )}
    </Stack>
  )
}
